import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset,
  shift,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'

import CopySvg from '@/assets/icons/copy.svg?react'
import DislikeSvg from '@/assets/icons/dislike.svg?react'
import LikeSvg from '@/assets/icons/like.svg?react'
import RefreshSvg from '@/assets/icons/refresh.svg?react'
import type {UseMessagesReturn} from '@/components/ChatSection/hooks/useMessages'
import type {Message} from '@/models/messageModel'
import {feedbackMessage} from '@/services/messageService'

import style from './InnoSearchMessageToolbar.module.scss'

interface Props {
  messageContentRef: RefObject<HTMLElement | null>
  canRegenerate: boolean
  action: Message['action'] | undefined
  id: Message['id'] | undefined
  submitMessage: UseMessagesReturn['submitMessage']
}

async function copyResponse(
  responseElement: HTMLElement,
  onSuccess?: () => void,
  onFailure?: (e: Error) => void,
) {
  try {
    const node = responseElement.cloneNode(true) as HTMLDivElement

    // Convert all products into raw products
    const productsList = node.querySelectorAll('div[data-is-products=true]')
    productsList.forEach(products => {
      const pElement = document.createElement('p')
      pElement.innerText = (products as HTMLDivElement).dataset.rawProducts ?? ''
      products.replaceWith(pElement)
    })

    // Add br after root child
    Array.from(node.children)
      .slice(0, -1)
      .forEach(child => {
        child.after(document.createElement('br'))
      })

    const clipboardItemInput = new ClipboardItem({
      'text/plain': new Blob([node.innerHTML], {type: 'text/plain'}),
      'text/html': new Blob([node.outerHTML], {type: 'text/html'}),
    })
    await navigator.clipboard.write([clipboardItemInput])
    onSuccess?.()
  } catch (e) {
    onFailure?.(e as Error)
  }
}

export default memo(function InnoSearchMessageToolbar({
  messageContentRef,
  canRegenerate,
  action,
  id,
  submitMessage,
}: Props) {
  const [isLikeOrDislike, setIsLikeOrDislike] = useState<undefined | boolean>()

  if (action === 'like' && isLikeOrDislike !== true) setIsLikeOrDislike(true)
  else if (action === 'dislike' && isLikeOrDislike !== false) setIsLikeOrDislike(false)

  const [isCopied, setIsCopied] = useReducer((_: boolean, next: boolean) => {
    if (next) {
      setTimeout(() => {
        setIsCopied(false)
      }, 2000)
    }
    return next
  }, false)

  const {refs, floatingStyles, context} = useFloating({
    strategy: 'absolute',
    open: isCopied,
    onOpenChange: setIsCopied,
    middleware: [offset(5), flip({fallbackAxisSideDirection: 'end'}), shift()],
    whileElementsMounted: autoUpdate,
  })

  const role = useRole(context)

  const {getReferenceProps, getFloatingProps} = useInteractions([role])

  const copy = useCallback(() => {
    if (!messageContentRef.current) return

    void copyResponse(
      messageContentRef.current,
      () => {
        setIsCopied(true)
      },
      e => {
        console.error('Copy message failed', e)
      },
    )
  }, [messageContentRef])

  const likeOrDislike = function (isLike: boolean) {
    let action: Message['action']

    if (isLike === isLikeOrDislike) {
      action = null
    } else {
      action = isLike ? 'like' : 'dislike'
    }

    setIsLikeOrDislike(isLike === isLikeOrDislike ? undefined : isLike)

    if (id) {
      // TODO: convert to mutation
      void feedbackMessage({
        messageId: id,
        action,
      })
    }
  }

  const regenerate = function () {
    submitMessage('', true)
  }

  return (
    <div className={style.MessageToolBar} role='list' aria-label='Actions on reply'>
      {canRegenerate && (
        <div role='listitem'>
          <button aria-label='Regenerate response' onClick={regenerate}>
            <RefreshSvg className={style.Stroke} aria-hidden />
          </button>
        </div>
      )}
      <div role='listitem'>
        <button
          aria-label='Copy response'
          onClick={copy}
          ref={refs.setReference}
          {...getReferenceProps({
            'aria-expanded': undefined,
            'aria-haspopup': undefined,
            'aria-controls': undefined,
          })}
        >
          <CopySvg className={style.Stroke} aria-hidden />
        </button>
        {isCopied && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              className={style.Popover}
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              <div className={style.PopoverArrow}></div>
              <div className={style.PopoverContent}>Copied!</div>
            </div>
          </FloatingFocusManager>
        )}
      </div>
      <div role='listitem'>
        <button
          aria-label='Like response'
          onClick={() => {
            likeOrDislike(true)
          }}
          role='switch'
          aria-checked={isLikeOrDislike === true}
        >
          <LikeSvg
            className={clsx(style.Stroke, isLikeOrDislike === true && style.Liked)}
            aria-hidden
          />
        </button>
      </div>
      <div role='listitem'>
        <button
          aria-label='Dislike response'
          onClick={() => {
            likeOrDislike(false)
          }}
          role='switch'
          aria-checked={isLikeOrDislike === false}
        >
          <DislikeSvg
            className={clsx(style.Stroke, isLikeOrDislike === false && style.Disliked)}
            aria-hidden
          />
        </button>
      </div>
    </div>
  )
})
